import React, { useMemo, useState } from "react";
import { jwtDecode } from "jwt-decode";
import eyeIcon from "./assets/eye.svg";

const AUTH_ENDPOINT = "https://www.fbxchange.com/ReactPHP/reactSearchNew.php";
const AUTH_STORAGE_KEY = "user2026";
const FBX_APP_URL = "/FBX2026/";

const redirectToFbxApp = () => {
  try {
    window.top.location.assign(FBX_APP_URL);
  } catch {
    window.location.assign(FBX_APP_URL);
  }
};

const getResetTokenFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const searchToken = params.get("token");

  if (searchToken) return searchToken;

  const hashQuery = window.location.hash.includes("?")
    ? window.location.hash.split("?")[1]
    : "";

  return new URLSearchParams(hashQuery).get("token") || "";
};

const isResetPath = () => {
  const path = window.location.pathname.toLowerCase();
  const hash = window.location.hash.toLowerCase();

  return path.includes("reset-password") || hash.includes("reset-password");
};

const postAuth = async (payload) => {
  const response = await fetch(AUTH_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(payload).toString(),
  });

  return response.json().catch(() => ({}));
};

const decodeUsableToken = (token) => {
  try {
    const decoded = jwtDecode(token);
    const now = Math.floor(Date.now() / 1000);

    if (
      decoded?.exp !== undefined &&
      !Number.isNaN(Number(decoded.exp)) &&
      Number(decoded.exp) < now
    ) {
      return null;
    }

    return decoded;
  } catch {
    return null;
  }
};

const saveAuthToken = (token) => {
  localStorage.setItem(AUTH_STORAGE_KEY, token);
};

const PasswordInput = ({ id, value, onChange, disabled, autoComplete }) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="password-field">
      <input
        id={id}
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder="Enter password"
        required
        disabled={disabled}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        className="icon-button"
        onClick={() => setVisible((current) => !current)}
        disabled={disabled}
        aria-label={visible ? "Hide password" : "Show password"}
        title={visible ? "Hide password" : "Show password"}
      >
        <img src={eyeIcon} alt="" aria-hidden="true" />
      </button>
    </div>
  );
};

const SignIn = ({ onResetMode }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState(false);

  const submitLogin = async (event) => {
    event.preventDefault();
    setMessage("");
    setWorking(true);

    try {
      const data = await postAuth({
        method: "signIn",
        user: username,
        userp: password,
      });
      const token = data?.user || data?.token || "";
      const decoded = token ? decodeUsableToken(token) : null;

      if (!data?.success || !token || !decoded) {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        setMessage("Invalid username or password.");
        setWorking(false);
        return;
      }

      saveAuthToken(token);
      redirectToFbxApp();
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setMessage("Sign in failed. Please try again.");
      setWorking(false);
    }
  };

  return (
    <form className="login-panel" onSubmit={submitLogin}>
      <div className="field-group">
        <label htmlFor="username">Username</label>
        <input
          id="username"
          type="text"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          placeholder="Enter username"
          required
          disabled={working}
          autoComplete="username"
        />
      </div>

      <div className="field-group">
        <label htmlFor="password">Password</label>
        <PasswordInput
          value={password}
          id="password"
          onChange={(event) => setPassword(event.target.value)}
          disabled={working}
          autoComplete="current-password"
        />
      </div>

      {message && <div className="form-message error">{message}</div>}

      <button className="primary-button" type="submit" disabled={working}>
        {working ? "Signing In..." : "Sign In"}
      </button>

      <button
        className="text-button"
        type="button"
        onClick={onResetMode}
        disabled={working}
      >
        Forgot password?
      </button>
    </form>
  );
};

const ForgotPassword = ({ onSignInMode }) => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState(false);

  const submitResetRequest = async (event) => {
    event.preventDefault();
    setMessage("");
    setWorking(true);

    try {
      const data = await postAuth({
        method: "requestPasswordReset",
        email,
      });

      setMessage(
        data?.message ||
          "If that email is on an active FBXchange account, a reset link has been sent."
      );
    } catch {
      setMessage("Could not send the reset email. Please try again.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <form className="login-panel" onSubmit={submitResetRequest}>
      <div className="field-group">
        <label htmlFor="reset-email">Email</label>
        <input
          id="reset-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Enter account email"
          required
          disabled={working}
          autoComplete="email"
        />
      </div>

      {message && <div className="form-message">{message}</div>}

      <button className="primary-button" type="submit" disabled={working}>
        {working ? "Sending..." : "Send Reset Link"}
      </button>

      <button
        className="text-button"
        type="button"
        onClick={onSignInMode}
        disabled={working}
      >
        Back to sign in
      </button>
    </form>
  );
};

const PasswordReset = ({ onComplete }) => {
  const token = useMemo(getResetTokenFromUrl, []);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [validating, setValidating] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [working, setWorking] = useState(false);

  React.useEffect(() => {
    let alive = true;

    const validateToken = async () => {
      if (!token) {
        setMessage("This reset link is invalid or has expired.");
        setValidating(false);
        return;
      }

      try {
        const data = await postAuth({
          method: "validatePasswordResetToken",
          token,
        });

        if (!alive) return;

        setValidToken(Boolean(data?.valid));
        setMessage(data?.message || "");
      } catch {
        if (alive) {
          setMessage("Could not validate this reset link.");
        }
      } finally {
        if (alive) {
          setValidating(false);
        }
      }
    };

    validateToken();

    return () => {
      alive = false;
    };
  }, [token]);

  const submitPasswordReset = async (event) => {
    event.preventDefault();
    setMessage("");

    if (password.length < 8) {
      setMessage("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    setWorking(true);

    try {
      const data = await postAuth({
        method: "resetPassword",
        token,
        password,
      });

      setMessage(data?.message || "Your password has been updated.");

      if (data?.success) {
        window.history.replaceState({}, "", "/");
        setTimeout(onComplete, 1200);
      }
    } catch {
      setMessage("Could not reset your password. Please try again.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <form className="login-panel" onSubmit={submitPasswordReset}>
      {validating ? (
        <div className="form-message">Checking reset link...</div>
      ) : (
        <>
          {message && <div className="form-message">{message}</div>}

          {validToken && (
            <>
              <div className="field-group">
                <label htmlFor="new-password">New Password</label>
                <PasswordInput
                  value={password}
                  id="new-password"
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={working}
                  autoComplete="new-password"
                />
              </div>

              <div className="field-group">
                <label htmlFor="confirm-password">Confirm Password</label>
                <PasswordInput
                  value={confirmPassword}
                  id="confirm-password"
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  disabled={working}
                  autoComplete="new-password"
                />
              </div>

              <button className="primary-button" type="submit" disabled={working}>
                {working ? "Updating..." : "Update Password"}
              </button>
            </>
          )}
        </>
      )}

      <button
        className="text-button"
        type="button"
        onClick={onComplete}
        disabled={working}
      >
        Back to sign in
      </button>
    </form>
  );
};

const App = () => {
  const [mode, setMode] = useState(isResetPath() ? "reset" : "signin");

  return (
    <main className="login-page">
      <section className="login-shell" aria-label="FBXchange sign in">
        {mode === "signin" && <SignIn onResetMode={() => setMode("forgot")} />}
        {mode === "forgot" && <ForgotPassword onSignInMode={() => setMode("signin")} />}
        {mode === "reset" && <PasswordReset onComplete={() => setMode("signin")} />}
      </section>
    </main>
  );
};

export default App;
